import { Permission } from 'settings/schema'
import * as yup from 'yup'
import { t } from 'translations'

interface PermissionDefinition {
    label: () => string
    prerequisites?: Permission[]
}

export const PERMISSION_DEFINITIONS: Record<Permission, PermissionDefinition> = {
    partner_admin: { label: () => t('settings.users.form.permissions.partnerAdmin') },
    access_products: { label: () => t('settings.users.form.permissions.accessProducts') },
    view_pricing_settings: {
        label: () => t('settings.users.form.permissions.viewPricingSettings'),
        prerequisites: ['access_products'],
    },
    edit_pricing_settings: {
        label: () => t('settings.users.form.permissions.editPricingSettings'),
        prerequisites: ['view_pricing_settings'],
    },
    view_product_validity: {
        label: () => t('settings.users.form.permissions.viewProductValidity'),
        prerequisites: ['access_products'],
    },
    edit_product_validity: {
        label: () => t('settings.users.form.permissions.editProductValidity'),
        prerequisites: ['view_product_validity'],
    },
    access_orders: {
        label: () => t('settings.users.form.permissions.accessOrders'),
        prerequisites: ['access_products'],
    },
    edit_orders: {
        label: () => t('settings.users.form.permissions.editOrders'),
        prerequisites: ['access_orders', 'view_pricing_settings'],
    },
    create_order: { label: () => t('settings.users.form.permissions.createOrder'), prerequisites: ['edit_orders'] },
    export_orders: { label: () => t('settings.users.form.permissions.exportOrders'), prerequisites: ['access_orders'] },
    refund_orders: { label: () => t('settings.users.form.permissions.refundOrders'), prerequisites: ['access_orders'] },
    read_carts: { label: () => t('settings.users.form.permissions.readCarts') },
    write_carts: { label: () => t('settings.users.form.permissions.writeCarts'), prerequisites: ['read_carts'] },
    control_panel_login: { label: () => t('settings.users.form.permissions.controlPanelLogin') },
    access_analytics: {
        label: () => t('settings.users.form.permissions.accessAnalytics'),
        prerequisites: ['control_panel_login'],
    },
    access_codes: {
        label: () => t('settings.users.form.permissions.accessCodes'),
        prerequisites: ['control_panel_login'],
    },
    view_wonderbar: {
        label: () => t('settings.users.form.permissions.viewWonderbar'),
        prerequisites: ['control_panel_login'],
    },
    edit_wonderbar: {
        label: () => t('settings.users.form.permissions.editWonderbar'),
        prerequisites: ['view_wonderbar'],
    },
    pos_login: {
        label: () => t('settings.users.form.permissions.posLogin'),
        prerequisites: ['write_carts', 'create_order'],
    },
    access_pulse: {
        label: () => t('settings.users.form.permissions.accessPulse'),
        prerequisites: ['control_panel_login'],
    },
    access_pulse_sales: {
        label: () => t('settings.users.form.permissions.accessPulseSales'),
        prerequisites: ['access_pulse'],
    },
    mcp: { label: () => t('settings.users.form.permissions.mcp'), prerequisites: ['access_pulse'] },
    access_ai_assistant: {
        label: () => t('settings.users.form.permissions.accessAiAssistant'),
        prerequisites: ['mcp'],
    },
}

export const AI_PERMISSIONS: Permission[] = ['access_pulse', 'access_pulse_sales', 'access_ai_assistant', 'mcp']

export const getPrerequisitePermissions = (permission: Permission, checked = new Set<Permission>()): Permission[] => {
    if (checked.has(permission)) {
        return []
    }
    checked.add(permission)

    const prerequisites = PERMISSION_DEFINITIONS[permission].prerequisites || []
    const allPrerequisites: Permission[] = [...prerequisites]
    for (const p of prerequisites) {
        allPrerequisites.push(...getPrerequisitePermissions(p, checked))
    }
    return [...new Set(allPrerequisites)]
}

export const getDependentPermissions = (permission: Permission, checked = new Set<Permission>()): Permission[] => {
    if (checked.has(permission)) {
        return []
    }
    checked.add(permission)

    const dependentPermissions: Permission[] = []
    for (const p of Object.keys(PERMISSION_DEFINITIONS)) {
        if (PERMISSION_DEFINITIONS[p as Permission].prerequisites?.includes(permission)) {
            dependentPermissions.push(p as Permission)
            dependentPermissions.push(...getDependentPermissions(p as Permission, checked))
        }
    }
    return [...new Set(dependentPermissions)]
}

export const userFormValidationSchema = yup.object().shape({
    email: yup
        .string()
        .email(() => t('settings.users.form.validation.emailInvalid'))
        .required(() => t('settings.users.form.validation.emailRequired')),
    resellerId: yup.string().when('role', {
        is: 'reseller',
        then: yup
            .string()
            .nullable()
            .required(() => t('settings.users.form.validation.resellerRequired')),
        otherwise: yup.string().nullable(),
    }),
    firstName: yup.string().when('permissions.pos_login', {
        is: true,
        then: yup.string().required(() => t('settings.users.form.validation.firstNameRequired')),
        otherwise: yup.string().nullable(),
    }),
    lastName: yup.string().when('permissions.pos_login', {
        is: true,
        then: yup.string().required(() => t('settings.users.form.validation.lastNameRequired')),
        otherwise: yup.string().nullable(),
    }),
})
